import React, { Dispatch, useEffect, useMemo, useReducer } from "react";
import { Outlet } from "react-router";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { defineMessages } from "react-intl";
import { IconAddressBook, IconAdjustmentsCog, IconWallet } from "@tabler/icons-react";
import { AnyPeriod, AnyPrice, castPeriod, GQLPeriod, isotizeDate, toGQLPeriod, useIsMounted } from "@ct-react/core";
import Wrapper from "../../../components/layout/wrapper";
import CheckoutStepper from "../../../components/cart/checkout-stepper";
import { CartCustomer, TaxableAgeCategoryInput } from "../../../models/cart";
import { AgeCategory } from "../../../models/options";

const transDefs = defineMessages({
  opt: { id: "interhome-stepper-opt-label", defaultMessage: "Options" },
  info: { id: "interhome-stepper-info-label", defaultMessage: "Vos informations" },
  pay: { id: "interhome-stepper-payment-label", defaultMessage: "Paiement" }
});

const steps = [
  { icon: IconAdjustmentsCog, msg: transDefs.opt },
  { icon: IconAddressBook, msg: transDefs.info },
  { icon: IconWallet, msg: transDefs.pay }
];

export enum InterhomePaymentMode { CC = "CreditCard", Invoice = "Invoice" }
export type InterhomePayment = {
  type: InterhomePaymentMode,
  amount: AnyPrice,
  dueDate?: string,
  balance?: AnyPrice,
  balanceDueDate?: string
};

type InterhomeChoice = { articleId: string, period: GQLPeriod };
type InterhomeServiceOption = { code: string, total: number };
type InterhomePriceResume = {
  total: AnyPrice;
  paymentModes: InterhomePayment[]
};

type InterhomeContext = {
  index?: number;
  item: InterhomeChoice;
  participants: TaxableAgeCategoryInput[];
  options?: InterhomeServiceOption[];
  customer?: CartCustomer;
  resume?: InterhomePriceResume;
}

type Action =
  { type: "change.index", payload: number } |
  { type: "change.participants", payload: TaxableAgeCategoryInput[] } |
  { type: "change.options", payload: InterhomeServiceOption } |
  { type: "change.resume", payload: InterhomePriceResume } |
  { type: "change.customer", payload: CartCustomer };

const reducer = (state: InterhomeContext, { type, payload }: Action): InterhomeContext => {
  switch (type) {
    case "change.index":
      return { ...state, index: payload };
    case "change.participants":
      return { ...state, participants: payload };
    case "change.options":
      const formatting = [
        ...state.options?.filter(opt => opt.code !== payload.code) || [],
        ...(payload.total > 0) ? [ payload ] : []
      ];
      return { ...state, options: formatting.length > 0 ? formatting : undefined };
    case "change.resume":
      return { ...state, resume: payload };
    case "change.customer":
      return { ...state, customer: payload };
  }
}

const useInitialState = (): InterhomeContext => {

  const { state } = useLocation();
  const [ searchParams ] = useSearchParams();

  return useMemo(() => ({
    item: {
      articleId: searchParams.get("refId")!,
      period: toGQLPeriod([ searchParams.get("checkin")!, searchParams.get("checkout")! ]),
    },
    participants: state?.participants || [ { ageCategory: AgeCategory.ADULT, number: 1 } ],
    ...(!!state?.options) && { options: state.options },
    ...(!!state?.customer && { customer: state.customer })
  }), []);

}

export const interhomeUrlParams = (articleId: string, period: AnyPeriod) => {
  const casted = castPeriod(period);
  return `refId=${articleId}&checkin=${isotizeDate(casted.start)}&checkout=${isotizeDate(casted.end)}`;
}

export const useInterhomeContext = () => useOutletContext<[ state: Readonly<InterhomeContext>, dispatch: Dispatch<Action>]>();

const InterhomeOutlet = () => {

  const isMounted = useIsMounted();
  const location = useLocation();
  const navigate = useNavigate();

  const initialState = useInitialState();
  const [ state, dispatch ] = useReducer(reducer, initialState);

  useEffect(() => {
    const { participants, options, customer } = state;
    isMounted && navigate(location, { replace: true, state: { ...location.state, participants, options, customer } });
  }, [ state ]);

  return (
    <div className="rl-cart">
      <Wrapper.Centered className="booking-process">
        <CheckoutStepper steps={steps} activeIndex={state.index} />
      </Wrapper.Centered>
      <Wrapper.Centered as="main">
        <Outlet context={[ state, dispatch ]} />
      </Wrapper.Centered>
    </div>);

}

export default InterhomeOutlet;
